import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, Shield, Clock, Target } from 'lucide-react'

interface ConsentStepProps {
  onNext: () => void
}

export function ConsentStep({ onNext }: ConsentStepProps) {
  const { t } = useTranslation()

  const features = [
    {
      icon: <Target className="h-5 w-5 text-emerald-600" />,
      title: t('budgetAssistant.consent.features.personalized.title', 'Personalized Recommendations'),
      description: t('budgetAssistant.consent.features.personalized.description', 'Get budget suggestions tailored to your income and spending patterns')
    },
    {
      icon: <Clock className="h-5 w-5 text-emerald-600" />,
      title: t('budgetAssistant.consent.features.quick.title', 'Quick Setup'),
      description: t('budgetAssistant.consent.features.quick.description', 'Complete the process in just 5-10 minutes with our guided wizard')
    },
    {
      icon: <Shield className="h-5 w-5 text-emerald-600" />,
      title: t('budgetAssistant.consent.features.secure.title', 'Secure & Private'),
      description: t('budgetAssistant.consent.features.secure.description', 'Your financial information is encrypted and never shared')
    },
    {
      icon: <CheckCircle className="h-5 w-5 text-emerald-600" />,
      title: t('budgetAssistant.consent.features.actionable.title', 'Actionable Results'),
      description: t('budgetAssistant.consent.features.actionable.description', 'Get ready-to-use budgets you can activate with one click')
    }
  ]

  const steps = [
    t('budgetAssistant.consent.steps.income', 'Share your monthly income'),
    t('budgetAssistant.consent.steps.expenses', 'Tell us about your regular expenses'),
    t('budgetAssistant.consent.steps.goals', 'Set your savings goals'),
    t('budgetAssistant.consent.steps.results', 'Review your personalized budget plan')
  ]

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Hero Section */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-16 h-16 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl p-3 shadow-lg">
            <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 01 7.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.consent.title', 'Welcome to Smart Budget Assistant')}
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('budgetAssistant.consent.subtitle', 'Let us help you create a personalized budget plan based on your unique financial situation.')}
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature, index) => (
          <Card key={index} className="p-5 border-emerald-100 dark:border-emerald-900/30 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                {feature.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 text-sm">
                  {feature.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Process Overview */}
      <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
        <h3 className="text-base font-semibold text-emerald-900 dark:text-emerald-100 mb-3">
          {t('budgetAssistant.consent.process.title', 'How it works')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-semibold">
                {index + 1}
              </div>
              <span className="text-xs text-emerald-800 dark:text-emerald-200">
                {step}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Privacy Notice */}
      <Card className="p-5 border-blue-100 dark:border-blue-900/30 bg-blue-50/50 dark:bg-blue-950/20">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1 text-sm">
              {t('budgetAssistant.consent.privacy.title', 'Your Privacy Matters')}
            </h4>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {t('budgetAssistant.consent.privacy.description', 'We only use the information you provide to generate your personalized budget recommendations. Your data is encrypted, stored securely, and never shared with third parties.')}
            </p>
          </div>
        </div>
      </Card>

      {/* CTA */}
      <div className="text-center">
        <Button
          onClick={onNext}
          className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 py-2 text-base shadow-lg h-11"
        >
          {t('budgetAssistant.consent.cta', 'Get Started')}
        </Button>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {t('budgetAssistant.consent.disclaimer', 'You can skip any step or exit at any time')}
        </p>
      </div>
    </div>
  )
} 